import { useFileStore } from "../../stores/fileStore";
import { collectResolvedXmlAssetsForZip } from "../../utils/environmentZipAssets";
import { logger } from "../../utils/logger";
import type { EnvBundleFingerprint } from "./types";
import { findFileMapKeyByBasename, readJsonFromFileMap, readTextFromFileMap } from "./fileMap";
import { sha256Hex } from "./hash";
import {
  canonicalizeXmlForEnvBundle,
  extractModelNameFromXml,
  preflightMissingMeshes,
  toSafeZipPath,
} from "./xml";

type JsonRecord = Record<string, unknown>;
type EnvConfigAssetFingerprint = {
  zipPath?: unknown;
  sha256?: unknown;
  byteLength?: unknown;
};

const isRecord = (value: unknown): value is JsonRecord =>
  !!value && typeof value === "object" && !Array.isArray(value);

let cachedEnvConfigFingerprint: {
  cacheKey: string;
  value: EnvBundleFingerprint | null;
} | null = null;
const envConfigFingerprintInFlightByKey = new Map<string, Promise<EnvBundleFingerprint | null>>();

function buildEnvConfigFingerprintCacheKey(fileMap: Record<string, File>) {
  const key = findFileMapKeyByBasename(fileMap, "env_config.json");
  if (!key) return "missing";
  const file = fileMap[key] || null;
  if (!file) return `missing-file:${key}`;
  return JSON.stringify({
    key,
    size: Number(file?.size) || 0,
    lastModified: Number(file?.lastModified) || 0,
    name: String(file?.name || ""),
  });
}

export async function tryReadEnvConfigFingerprintFromFileMap(
  fileMap: Record<string, File>
): Promise<EnvBundleFingerprint | null> {
  const cacheKey = buildEnvConfigFingerprintCacheKey(fileMap);
  if (cachedEnvConfigFingerprint?.cacheKey === cacheKey) return cachedEnvConfigFingerprint.value;

  const inFlight = envConfigFingerprintInFlightByKey.get(cacheKey);
  if (inFlight) return inFlight;

  const loadPromise = (async () => {
    try {
      const key = findFileMapKeyByBasename(fileMap, "env_config.json");
      const cfg = await readJsonFromFileMap(fileMap, key);
      const env = isRecord(cfg) && isRecord(cfg.env) ? cfg.env : null;
      const fp = env && isRecord(env.fingerprint) ? env.fingerprint : null;
      if (!env || !fp) return null;

      const envId =
        typeof env.envId === "string" && env.envId.trim() ? env.envId.trim() : "critterapp-env";
      const modelName =
        typeof env.modelName === "string" && env.modelName.trim().length
          ? String(env.modelName).trim()
          : null;
      const xmlSha256 =
        typeof fp.xmlSha256 === "string" && fp.xmlSha256.trim()
          ? fp.xmlSha256.trim().toLowerCase()
          : "";
      const bundleSha256 =
        typeof fp.bundleSha256 === "string" && fp.bundleSha256.trim()
          ? fp.bundleSha256.trim().toLowerCase()
          : "";
      const assetsRaw: unknown[] | null = Array.isArray(fp.assets) ? fp.assets : null;

      if (!xmlSha256 || !bundleSha256 || !assetsRaw) return null;

      const assets = assetsRaw
        .map((rawAsset) => {
          const a = rawAsset as EnvConfigAssetFingerprint;
          const zipPath = typeof a?.zipPath === "string" ? a.zipPath : "";
          const sha256 = typeof a?.sha256 === "string" ? a.sha256.toLowerCase() : "";
          const byteLength = Number(a?.byteLength);
          if (!zipPath || !sha256 || !Number.isFinite(byteLength) || byteLength < 0) return null;
          return { zipPath, sha256, byteLength };
        })
        .filter(Boolean) as EnvBundleFingerprint["assets"];

      if (!assets.length) return null;
      assets.sort((a, b) => a.zipPath.localeCompare(b.zipPath));

      return { envId, modelName, xmlSha256, assets, bundleSha256 };
    } catch {
      return null;
    }
  })();

  envConfigFingerprintInFlightByKey.set(cacheKey, loadPromise);
  try {
    const value = await loadPromise;
    cachedEnvConfigFingerprint = { cacheKey, value };
    return value;
  } finally {
    envConfigFingerprintInFlightByKey.delete(cacheKey);
  }
}

let cachedEnvBundleFingerprint: {
  cacheKey: string;
  value: EnvBundleFingerprint;
} | null = null;
const envBundleFingerprintInFlightByKey = new Map<string, Promise<EnvBundleFingerprint>>();

export function shouldSkipFingerprintAsset(zipPath: string) {
  const baseName = zipPath.split("/").pop()?.toLowerCase() || "";
  return (
    baseName === "env_config.json" ||
    baseName === "scene_config.json" ||
    baseName === "warmstart_dataset.jsonl" ||
    baseName === ".ds_store" ||
    zipPath.toLowerCase().startsWith("__macosx/")
  );
}

function buildEnvBundleFingerprintCacheKey(args: {
  xmlSha256: string;
  fileMap: Record<string, File>;
}) {
  const assets = Object.entries(args.fileMap || {})
    .filter(([name]) => !String(name).toLowerCase().endsWith(".xml"))
    .map(([name, file]) => {
      const zipPath = toSafeZipPath(name);
      return { zipPath, file };
    })
    .filter(({ zipPath }) => !shouldSkipFingerprintAsset(zipPath))
    .map(({ zipPath, file }) => ({
      zipPath,
      size: Number(file?.size) || 0,
      lastModified: Number(file?.lastModified) || 0,
      name: String(file?.name || ""),
    }))
    .sort((a, b) => a.zipPath.localeCompare(b.zipPath));

  return JSON.stringify({
    xmlSha256: args.xmlSha256,
    assets,
  });
}

export async function computeEnvBundleFingerprint(): Promise<EnvBundleFingerprint> {
  const fileState = useFileStore.getState();
  const fileMap = fileState.fileMap || {};

  // If we're running against an exported env bundle zip, prefer its embedded fingerprint for compatibility checks.
  // This avoids subtle hash differences caused by regenerating XML from scene state (ordering/float formatting).
  const envConfigFp = await tryReadEnvConfigFingerprintFromFileMap(fileMap);

  // Prefer hashing `model.xml` from the bundle when present (env bundles can contain multiple .xml files).
  const modelXmlKey = findFileMapKeyByBasename(fileMap, "model.xml");
  const xmlFromFileMap = await readTextFromFileMap(fileMap, modelXmlKey);
  const xmlRaw =
    xmlFromFileMap ||
    (fileState.xmlFile && typeof fileState.xmlFile.text === "function"
      ? await fileState.xmlFile.text()
      : await fileState.actions.generateXmlFromState());
  const xml = canonicalizeXmlForEnvBundle(xmlRaw);
  const modelName = extractModelNameFromXml(xml);
  const envId = envConfigFp?.envId || modelName || "critterapp-env";

  preflightMissingMeshes(xml, fileMap);

  const xmlSha256 = await sha256Hex(xml);
  const cacheKey = buildEnvBundleFingerprintCacheKey({ xmlSha256, fileMap });

  if (cachedEnvBundleFingerprint?.cacheKey === cacheKey) {
    return cachedEnvBundleFingerprint.value;
  }

  const inFlight = envBundleFingerprintInFlightByKey.get(cacheKey);
  if (inFlight) return inFlight;

  if (envConfigFp) {
    if (envConfigFp.xmlSha256 !== xmlSha256) {
      // Avoid silent fallbacks that hide bugs: warn and proceed to recompute.
      logger.warn(
        "[env-fingerprint] env_config.xmlSha256 mismatch vs current canonical XML; recomputing",
        {
          envConfigXmlSha256: envConfigFp.xmlSha256,
          computedXmlSha256: xmlSha256,
        }
      );
    } else {
      // Use the exact bundleSha256 that was exported (what rollout servers should report).
      const result = {
        envId,
        modelName: envConfigFp.modelName ?? modelName,
        xmlSha256,
        assets: envConfigFp.assets,
        bundleSha256: envConfigFp.bundleSha256,
      };
      cachedEnvBundleFingerprint = {
        cacheKey,
        value: result,
      };
      return result;
    }
  }

  const computePromise = (async () => {
    const assets: EnvBundleFingerprint["assets"] = [];

    for (const asset of await collectResolvedXmlAssetsForZip(xml, fileMap)) {
      const zipPath = asset.zipPath;
      // Env bundle fingerprints intentionally ignore metadata files that may be present in the zip,
      // like env_config.json and warmstart_dataset.jsonl (extraZipEntries).
      if (shouldSkipFingerprintAsset(zipPath)) continue;
      const bytes = new Uint8Array(await asset.file.arrayBuffer());
      assets.push({
        zipPath,
        byteLength: bytes.byteLength,
        sha256: await sha256Hex(bytes),
      });
    }

    assets.sort((a, b) => a.zipPath.localeCompare(b.zipPath));
    const bundleSha256 = await sha256Hex(
      JSON.stringify({
        xmlSha256,
        assets,
      })
    );

    return { envId, modelName, xmlSha256, assets, bundleSha256 };
  })();

  envBundleFingerprintInFlightByKey.set(cacheKey, computePromise);
  try {
    const result = await computePromise;
    cachedEnvBundleFingerprint = {
      cacheKey,
      value: result,
    };
    return result;
  } finally {
    envBundleFingerprintInFlightByKey.delete(cacheKey);
  }
}

export async function detectEnvConfigXmlFingerprintMismatch(xmlRaw?: string): Promise<boolean> {
  const fileState = useFileStore.getState();
  const fileMap = fileState.fileMap || {};
  const envConfigFp = await tryReadEnvConfigFingerprintFromFileMap(fileMap);
  if (!envConfigFp?.xmlSha256) return false;

  const modelXmlKey = findFileMapKeyByBasename(fileMap, "model.xml");
  const xmlFromFileMap = xmlRaw == null ? await readTextFromFileMap(fileMap, modelXmlKey) : null;
  const sourceXml =
    xmlRaw ??
    xmlFromFileMap ??
    (fileState.xmlFile && typeof fileState.xmlFile.text === "function"
      ? await fileState.xmlFile.text()
      : await fileState.actions.generateXmlFromState());
  const xmlSha256 = await sha256Hex(canonicalizeXmlForEnvBundle(sourceXml));
  return envConfigFp.xmlSha256 !== xmlSha256;
}
